/**
 * CTRL_10 Plugin - AI Documentation Completeness
 * Custom UI with documentation coverage tracking and missing sections alerts
 * Extends GenericConfigPanel with documentation checklist
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Alert,
  List,
  Tag,
  Space,
  Statistic,
  Checkbox,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Documentation Coverage Dashboard Component
 */
function DocumentationDashboard({ config, projectId }) {
  const [sections, setSections] = useState([]);
  const [coverage, setCoverage] = useState({
    complete: 0,
    incomplete: 0,
    total: 0,
  });

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchDocumentation();
    }
  }, [config, projectId]);

  const fetchDocumentation = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      const docs = [
        { section: 'Model Purpose', status: 'complete', required: true },
        { section: 'Data Sources', status: 'complete', required: true },
        { section: 'Training Process', status: 'complete', required: true },
        { section: 'Performance Metrics', status: 'incomplete', required: true },
        { section: 'Limitations', status: 'incomplete', required: true },
        { section: 'Intended Use', status: 'complete', required: true },
        { section: 'Risk Assessment', status: 'missing', required: false },
      ];
      setSections(docs);
      setCoverage({
        complete: docs.filter((d) => d.status === 'complete').length,
        incomplete: docs.filter((d) => d.status !== 'complete').length,
        total: docs.length,
      });
    }, 500);
  };

  if (!config?.enabled) {
    return (
      <Alert
        message="Documentation Tracking Disabled"
        description="Enable to track AI model documentation completeness."
        type="info"
        showIcon
      />
    );
  }

  const coveragePercent = coverage.total > 0 ? ((coverage.complete / coverage.total) * 100).toFixed(1) : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Coverage Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Documentation Coverage"
              value={coveragePercent}
              suffix="%"
              prefix={<FileTextOutlined />}
              valueStyle={{ color: coveragePercent >= 80 ? colors.status.pass : colors.status.warning }}
            />
            <Progress
              percent={coveragePercent}
              strokeColor={coveragePercent >= 80 ? colors.status.pass : colors.status.warning}
              size="small"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Complete Sections"
              value={coverage.complete}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Missing/Incomplete"
              value={coverage.incomplete}
              prefix={<WarningOutlined />}
              valueStyle={{ color: colors.status.warning }}
            />
          </Card>
        </Col>
      </Row>

      {/* Documentation Checklist */}
      <Card size="small" title="Documentation Sections">
        <List
          size="small"
          dataSource={sections}
          renderItem={(section) => {
            const icon =
              section.status === 'complete' ? (
                <CheckCircleOutlined style={{ color: colors.status.pass }} />
              ) : section.status === 'incomplete' ? (
                <WarningOutlined style={{ color: colors.status.warning }} />
              ) : (
                <CloseCircleOutlined style={{ color: colors.status.fail }} />
              );

            const tagColor =
              section.status === 'complete' ? 'green' : section.status === 'incomplete' ? 'orange' : 'red';

            return (
              <List.Item>
                <Space>
                  {icon}
                  <span>{section.section}</span>
                  {section.required && <Tag color="blue">Required</Tag>}
                  <Tag color={tagColor}>{section.status}</Tag>
                </Space>
              </List.Item>
            );
          }}
        />
      </Card>

      {coverage.incomplete > 0 && (
        <Alert
          message={`${coverage.incomplete} section(s) need attention`}
          description="Complete all required documentation sections to achieve full compliance."
          type="warning"
          showIcon
        />
      )}
    </Space>
  );
}

/**
 * CTRL_10 Plugin Component
 */
function CTRL_10_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    required_sections: ['purpose', 'data_sources', 'training_process'],
  });

  return (
    <div>
      {/* Documentation Dashboard */}
      <DocumentationDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_10_Plugin;
